import type { Dance, Performance, PerformancePart, Recital } from "@recital-master/shared";
import {
  danceMasterPlaylistKey,
  danceStoryboardVttKey,
  danceThumbKey,
  slugify
} from "@recital-master/shared";

import type { Catalog } from "@/data/catalog";

/**
 * Bundled design-time catalog used when no dev media server is running.
 * Same shapes as generated catalog.json, no playable media.
 */

const STUDIO_ID = "westside-dance";

const danceTitles = [
  "A Very Merry Opening",
  "Slow Me Down",
  "Feelz Like Summer",
  "I Will Step",
  "Moonlight Sonata",
  "Tiny Tap Parade",
  "Golden Hour",
  "Rhythm Lab",
  "Paper Rings",
  "Storm Chasers",
  "Velvet Waltz",
  "Neon Nights",
  "The Sparrow",
  "Swing Street",
  "Wildflowers",
  "Midnight Train",
  "Little Dreamers",
  "Firefly Finale"
];

const styles = [
  "Jazz", "Lyrical", "Tap", "Ballet", "Contemporary", "Hip Hop",
  "Musical Theatre", "Acro", "Pointe"
];

const classNames = [
  "Mini Group", "Junior A", "Junior B", "Teen Company", "Senior Company",
  "Primary Ballet", "Adult Group", "Intermediate Group", "Senior Solo"
];

const dancerNames = [
  "Avery North", "Mila Rowan", "Nora Vale", "Lily Hart", "Emma Briar",
  "Zoe Marlow", "Olivia Wren", "Grace Linden", "Sofia Lane", "Harper Quinn",
  "Chloe Meadow", "Isla Mercer", "Ruby Lake", "Ella Winter", "Maya Finch",
  "Aria Monroe", "Scarlett Bloom", "Emily Arden", "Madison Cove", "Abigail Skye"
];

function buildDances(recitalId: string, performanceId: string, parts: PerformancePart[]): Dance[] {
  const dances: Dance[] = [];
  let programNumber = 0;
  parts.forEach((part) => {
    for (let i = 1; i <= part.danceCount; i++) {
      programNumber += 1;
      const title = danceTitles[(programNumber * 3 + performanceId.length) % danceTitles.length];
      const dancerCount = 3 + ((programNumber * 7) % 10);
      const offset = (programNumber * 5) % dancerNames.length;
      const dancers = Array.from({ length: dancerCount }, (_, d) => {
        const name = dancerNames[(offset + d) % dancerNames.length];
        return {
          dancerId: `${STUDIO_ID}-${recitalId}-${slugify(name)}`,
          name,
          slug: slugify(name)
        };
      });
      const danceId = `${performanceId}-${part.partId}-${String(i).padStart(3, "0")}`;
      const style = styles[programNumber % styles.length];
      dances.push({
        danceId,
        performanceId,
        recitalId,
        studioId: STUDIO_ID,
        partId: part.partId,
        danceNumber: programNumber,
        partDanceNumber: i,
        title,
        styles: [style],
        className: classNames[programNumber % classNames.length],
        durationSeconds: 96 + ((programNumber * 37) % 110),
        hlsPath: danceMasterPlaylistKey(STUDIO_ID, recitalId, performanceId, danceId),
        audioTracks: [],
        thumbPath: danceThumbKey(STUDIO_ID, recitalId, performanceId, danceId),
        storyboardVttPath: danceStoryboardVttKey(STUDIO_ID, recitalId, performanceId, danceId),
        blurThumb: null,
        dancers
      });
    }
  });
  return dances;
}

function buildPerformance(
  recitalId: string,
  performanceId: string,
  title: string,
  subtitle: string | null,
  partDanceCounts: number[]
): { performance: Performance; dances: Dance[] } {
  const parts: PerformancePart[] = partDanceCounts.map((count, index) => ({
    partId: `part-${index + 1}`,
    order: index + 1,
    title: `Part ${index + 1}`,
    danceCount: count,
    durationSeconds: count * 150
  }));
  const dances = buildDances(recitalId, performanceId, parts);
  const totalDurationSeconds = dances.reduce((sum, dance) => sum + dance.durationSeconds, 0);
  return {
    performance: {
      performanceId,
      recitalId,
      studioId: STUDIO_ID,
      title,
      subtitle,
      studioName: "Westside Dance Academy",
      eventDate: "2026-06-13",
      featuredDanceIds: [dances[0].danceId, dances[1].danceId, dances[2].danceId],
      published: true,
      parts,
      danceCount: dances.length,
      totalDurationSeconds
    },
    dances
  };
}

function buildDancerIndex(recitalId: string, performanceId: string, dances: Dance[]) {
  const byId = new Map<string, { dancerId: string; name: string; slug: string; danceIds: string[] }>();
  dances.forEach((dance) => {
    dance.dancers.forEach((dancer) => {
      const entry = byId.get(dancer.dancerId) ?? { ...dancer, danceIds: [] };
      entry.danceIds.push(dance.danceId);
      byId.set(dancer.dancerId, entry);
    });
  });
  return {
    performanceId,
    recitalId,
    dancers: [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  };
}

const recitals: Recital[] = [
  {
    recitalId: "season-2026",
    studioId: STUDIO_ID,
    studioSlug: STUDIO_ID,
    title: "In the Spotlight",
    year: 2026,
    slug: "in-the-spotlight-2026",
    coverImagePath: null,
    artColors: ["#2B3B8F", "#C2418F", "#F2B4D0"],
    intro: null
  },
  {
    recitalId: "season-2025",
    studioId: STUDIO_ID,
    studioSlug: STUDIO_ID,
    title: "Magical Moments",
    year: 2025,
    slug: "magical-moments-2025",
    coverImagePath: null,
    artColors: ["#123B33", "#C29A3B", "#EFE3C0"],
    intro: null
  }
];

const built = [
  { ...buildPerformance("season-2026", "recital-1", "Recital 1", "Day One", [12, 11]) },
  { ...buildPerformance("season-2026", "recital-2", "Recital 2", "Day Two", [14]) },
  { ...buildPerformance("season-2026", "recital-3", "Recital 3", "Day Three", [10, 12]) },
  { ...buildPerformance("season-2025", "recital-2025-1", "Recital 1", null, [13]) },
  { ...buildPerformance("season-2025", "recital-2025-2", "Recital 2", null, [12, 9]) }
];

export const sampleCatalog: Catalog = {
  studios: [{ studioId: STUDIO_ID, name: "Westside Dance Academy", slug: STUDIO_ID }],
  recitals,
  performances: built.map((entry) => entry.performance),
  dances: Object.fromEntries(built.map((entry) => [entry.performance.performanceId, entry.dances])),
  dancerIndexes: Object.fromEntries(
    built.map((entry) => [entry.performance.performanceId, buildDancerIndex(entry.performance.recitalId, entry.performance.performanceId, entry.dances)])
  )
};
